import React, { useState } from "react";
import { AffiliateRulesSection } from "./styles";
import { Box, Container, Divider, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { COMMISSION_TIERS } from "../MyAccount/Affiliate/commission";
import { formatCurrency } from "../../utils/formatCurrency";

type Lang = "EN" | "AR";

const AffiliateRules = () => {
	const [lang, setLang] = useState<Lang>("AR");
	const isAr = lang === "AR";

	return (
		<Box pt={4}>
			<AffiliateRulesSection dir={isAr ? "rtl" : "ltr"}>
				<Container maxWidth={"md"}>
					<Box display={"flex"} justifyContent={"flex-end"} mb={2}>
						<ToggleButtonGroup
							size={"small"}
							value={lang}
							exclusive
							onChange={(_, value) => value && setLang(value)}
							aria-label={"language toggle"}
							style={{ direction: "ltr" }}
						>
							<ToggleButton value={"EN"}>EN</ToggleButton>
							<ToggleButton value={"AR"}>AR</ToggleButton>
						</ToggleButtonGroup>
					</Box>
					<Box display={"flex"} flexDirection={"column"}>
						<Typography variant={"h2"} className={"page-title"}>
							{isAr ? "قواعد برنامج التسويق بالعمولة" : "Affiliate Program Rules"}
						</Typography>
						<Typography className={"effective-date"}>
							{isAr ? "تاريخ السريان: 28 يوليو 2026" : "Effective Date: July 28, 2026"}
						</Typography>

						<Typography className={"policy-intro"}>
							{isAr
								? "تحكم هذه القواعد المشاركة في برنامج VitesLink للتسويق بالعمولة. عند التسجيل كمسوّق بالعمولة، فإنك تقر بأنك قرأت هذه القواعد وفهمتها ووافقت عليها بالكامل، وتلتزم بالبقاء ملتزمًا بها طوال فترة نشاط حسابك. تحتفظ VitesLink بحق مراجعة أي حساب مسوّق أو الموافقة عليه أو تعليقه أو إنهائه وفق تقديرها الخاص."
								: "These rules govern participation in the VitesLink Affiliate Program. By signing up for an affiliate account, you confirm that you have read, understood, and agreed to all of the rules listed on this page, and you agree to remain bound by them for as long as your affiliate account remains active. VitesLink reserves the right to review, approve, suspend, or terminate any affiliate account at its sole discretion."}
						</Typography>

						<Divider sx={{ mb: 4 }} />

						{/* Section 1 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "1. شروط الأهلية" : "1. Eligibility"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? "للانضمام إلى برنامج التسويق بالعمولة، يجب أن:" : "To join the affiliate program, you must:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>لديك حساب VitesLink مسجَّل وفي وضع جيد.</li>
										<li>
											تكون مقيمًا في <strong>مصر</strong>. البرنامج متاح حاليًا للمسوّقين المقيمين في مصر فقط، وسيتم
											توسيعه ليشمل دولًا أخرى مستقبلاً.
										</li>
										<li>تقدّم بيانات تجارية وبيانات صرف دقيقة عند التقديم.</li>
										<li>تحصل على موافقة VitesLink قبل تفعيل كود الخصم الخاص بك.</li>
									</>
								) : (
									<>
										<li>Have a registered VitesLink account in good standing.</li>
										<li>
											Be based in <strong>Egypt</strong>. The affiliate program is currently open to affiliates based in
											Egypt only and will be expanded to other countries in the future.
										</li>
										<li>Provide accurate business and payout information when applying.</li>
										<li>Be approved by VitesLink before your coupon code becomes active.</li>
									</>
								)}
							</ul>
							<Typography className={"section-body"} mt={2}>
								{isAr
									? "تملك VitesLink حق رفض أي طلب أو إلغاء الموافقة عليه في أي وقت دون إبداء الأسباب."
									: "VitesLink may decline or revoke an application at any time without providing a reason."}
							</Typography>
						</Box>

						{/* Section 2 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "2. القيد الجغرافي" : "2. Geographic Restriction"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										ينطبق هذا القيد فقط على من يحق له أن يصبح مسوّقًا بالعمولة: يقتصر التسجيل والاحتفاظ بحساب مسوّق على
										الأفراد أو المنشآت المقيمة في <strong>مصر</strong>.
									</>
								) : (
									<>
										This restriction applies only to who can become an affiliate: only individuals or businesses based
										in <strong>Egypt</strong> may register for and hold an affiliate account.
									</>
								)}
							</Typography>
							<Typography className={"section-body"} mt={2}>
								{isAr
									? "لا يحدّ هذا القيد من مصدر مبيعاتك. فالدعوات التي تُشترى باستخدام كود الخصم الخاص بك مؤهلة بالكامل لاحتساب العمولة بغض النظر عن موقع المشتري أو الدعوة نفسها — فالمبيعات من خارج مصر تُعامل تمامًا كالمبيعات من داخلها."
									: "It does not restrict where your sales come from. Invitations purchased through your coupon code are fully eligible for commission regardless of where the buyer or the invitation itself is based — a buyer or invitation outside Egypt is treated no differently than one inside Egypt."}
							</Typography>
							<Typography className={"section-body"} mt={2}>
								{isAr
									? "هذا القيد مؤقت وليس دائمًا — تخطط VitesLink لتوسيع البرنامج ليشمل دولًا إضافية مستقبلاً، وسيتم إخطار المسوّقين عبر هذه الصفحة وحساباتهم فور إتاحة دول جديدة."
									: "This is a current limitation of the program, not a permanent one — VitesLink plans to expand the affiliate program to other countries in the future. Affiliates will be notified via this page and their account once additional countries become eligible."}
							</Typography>
						</Box>

						{/* Section 3 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "3. كود الخصم الخاص بك" : "3. Your Affiliate Coupon"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>يُمنح كل مسوّق كود خصم واحد وفريد بعد الموافقة على طلبه.</li>
										<li>لا يمكن تغيير كود الخصم بعد إنشائه، ولا يجوز نقله إلى شخص أو منشأة أخرى.</li>
										<li>تُحتسب المبيعة ضمن عمولتك فقط إذا استخدم المشتري كود الخصم الخاص بك عند إتمام الشراء.</li>
										<li>لا يجوز استخدام كود الخصم الخاص بك للشراء لنفسك أو لأي حساب تملكه أو تتحكم فيه.</li>
									</>
								) : (
									<>
										<li>Each affiliate is issued a single, unique coupon code upon approval.</li>
										<li>
											Your coupon code cannot be changed once created and may not be transferred to another person or
											business.
										</li>
										<li>
											A sale only counts toward your commission if the buyer applies your coupon code at checkout.
										</li>
										<li>
											You may not use your own coupon code to purchase invitations for yourself or for accounts you
											control.
										</li>
									</>
								)}
							</ul>
						</Box>

						{/* Section 4 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "4. العمولة" : "4. Commission"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										تُحتسب العمولة على أساس <strong>شرائح متدرجة</strong>، وفقًا لإجمالي مبيعاتك المؤهلة خلال الشهر، بعد
										خصم أي مبالغ مستردة أو ملغاة أو معكوسة. ينطبق معدل كل شريحة فقط على الجزء من مبيعاتك الواقع ضمنها —
										وليس على إجمالي مبيعات الشهر بالكامل.
									</>
								) : (
									<>
										Commission is calculated on a <strong>tiered</strong> basis, according to your total eligible sales
										volume within a given month, net of any refunds, cancellations, or chargebacks. Each tier's rate
										applies only to the portion of your sales that falls within that tier — not to your entire monthly
										total.
									</>
								)}
							</Typography>
							<ul>
								{COMMISSION_TIERS.map((tier, index) => {
									const lowerBound = index === 0 ? 0 : COMMISSION_TIERS[index - 1].upTo;
									const range = Number.isFinite(tier.upTo)
										? `${formatCurrency(lowerBound, "EGP")} – ${formatCurrency(tier.upTo, "EGP")}`
										: isAr
											? `أكثر من ${formatCurrency(lowerBound, "EGP")}`
											: `Above ${formatCurrency(lowerBound, "EGP")}`;
									return (
										<li key={tier.upTo}>
											{range}: <strong>{(tier.rate * 100).toFixed(0)}%</strong> {isAr ? "عمولة" : "commission"}
										</li>
									);
								})}
							</ul>
							<Typography className={"section-body"} mt={2}>
								{isAr
									? "يمكنك الاطلاع على معدل عمولتك الحالي وإجمالي مبيعاتك وأرباحك في أي وقت من لوحة تحكم المسوّق الخاصة بك."
									: "Your current commission rate and a running total of your sales and earnings are available at all times from your Affiliate Dashboard."}
							</Typography>
							<Typography className={"section-body"} mt={2}>
								{isAr
									? "تُخصم قيمة الطلبات المستردة أو الملغاة بعد الشراء من رصيد عمولتك، حتى لو كانت عمولة هذا الطلب قد احتُسبت ضمن إجمالي فترة سابقة."
									: "Orders that are refunded or cancelled after purchase are deducted from your commission balance, including in cases where the commission for that order has already been included in a prior period's total."}
							</Typography>
							<Box className={"highlight-box"} mt={2}>
								<p>
									{isAr ? (
										<>
											<strong>ملاحظة:</strong> تملك VitesLink حق تغيير شرائح العمولة وحدودها ومعدلاتها في أي وقت دون
											إشعار مسبق. يتم احتساب عمولة كل عملية بيع وفق المعدلات السارية وقت إتمامها.
										</>
									) : (
										<>
											<strong>Note:</strong> VitesLink may change these commission tiers, thresholds, and rates at any
											time without prior notice. The rates in effect at the time a sale is made are the rates used to
											calculate that sale's commission.
										</>
									)}
								</p>
							</Box>
						</Box>

						{/* Section 5 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "5. جدول التسوية" : "5. Settlement Schedule"}</Typography>
							<Typography className={"section-body"}>
								{isAr ? (
									<>
										تُسوّى العمولات على أساس <strong>شهري</strong>. تتم تسوية المبيعات المحققة خلال شهر ميلادي معين خلال{" "}
										<strong>الأسبوع الأول من الشهر التالي</strong>.
									</>
								) : (
									<>
										Commissions are settled on a <strong>monthly</strong> basis. Sales generated during a calendar month
										are settled during the <strong>first week of the following month</strong>.
									</>
								)}
							</Typography>
							<Box className={"highlight-box"} mt={2}>
								<p>
									{isAr ? (
										<>
											<strong>مثال:</strong> المبيعات التي تتم خلال شهر يوليو تُسوّى بحلول 7 أغسطس، والمبيعات التي تتم
											خلال أغسطس تُسوّى بحلول 7 سبتمبر، وهكذا.
										</>
									) : (
										<>
											<strong>Example:</strong> Sales made throughout July are settled by the 7th of August. Sales made
											throughout August are settled by the 7th of September, and so on.
										</>
									)}
								</p>
							</Box>
							<Typography className={"section-body"} mt={2}>
								{isAr
									? "تُصرف قيمة التسوية إلى وسيلة الصرف (حساب بنكي أو محفظة إلكترونية) المسجلة في ملفك كمسوّق. قد تتأخر مواعيد التسوية قليلاً بسبب عطلات نهاية الأسبوع أو الإجازات الرسمية أو أوقات معالجة مزودي خدمات الدفع."
									: "A settlement is paid out to the payout method (bank account or wallet) provided on your affiliate profile. Settlement dates may shift slightly to account for weekends, public holidays, or payment provider processing times."}
							</Typography>
						</Box>

						{/* Section 6 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "6. بيانات الصرف" : "6. Payout Information"}</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "أنت المسؤول عن الحفاظ على دقة بيانات الصرف الخاصة بك وتحديثها باستمرار. لا تتحمل VitesLink مسؤولية أي تأخير أو فشل في التسوية ناتج عن بيانات صرف غير صحيحة أو قديمة."
									: "You are responsible for keeping your payout details accurate and up to date. VitesLink is not responsible for delayed or failed settlements caused by incorrect or outdated payout information."}
							</Typography>
						</Box>

						{/* Section 7 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "7. الأنشطة المحظورة" : "7. Prohibited Activities"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "يُحظر تمامًا القيام بما يلي، وقد يؤدي إلى تعليق أو إنهاء الحساب فورًا:"
									: "The following are strictly prohibited and may result in immediate suspension or termination:"}
							</Typography>
							<ul>
								{isAr ? (
									<>
										<li>الشراء لنفسك أو استخدام كود الخصم الخاص بك في مشترياتك الشخصية.</li>
										<li>إرسال رسائل مزعجة، أو الإعلان بشكل مضلل، أو انتحال صفة VitesLink.</li>
										<li>المزايدة على اسم علامة VitesLink التجارية في إعلانات البحث المدفوعة.</li>
										<li>تسجيل حساب مسوّق أو الاحتفاظ به أثناء الإقامة خارج الدول المؤهلة حاليًا للبرنامج.</li>
										<li>إنشاء أكثر من حساب مسوّق واحد للالتفاف على هذه القواعد.</li>
										<li>أي نشاط احتيالي أو تعسفي أو تلاعبي يهدف إلى تضخيم المبيعات أو العمولة.</li>
									</>
								) : (
									<>
										<li>Self-referrals or using your own coupon code on your own purchases.</li>
										<li>Spamming, misleading advertising, or impersonating VitesLink.</li>
										<li>Bidding on VitesLink's brand name in paid search advertising.</li>
										<li>
											Registering or holding an affiliate account while based outside of the countries currently
											eligible for the program.
										</li>
										<li>Creating multiple affiliate accounts to circumvent these rules.</li>
										<li>Any fraudulent, abusive, or manipulative activity intended to inflate sales or commission.</li>
									</>
								)}
							</ul>
						</Box>

						{/* Section 8 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "8. التعليق والإنهاء" : "8. Suspension & Termination"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تملك VitesLink حق تعليق أو إنهاء حساب المسوّق في أي وقت، بإشعار أو دونه، عند مخالفة هذه القواعد أو لأي سبب آخر وفق تقديرها الخاص. يجوز حجب أو عكس أي عمولة تحققت من نشاط احتيالي أو تعسفي أو غير مؤهل، حتى لو كانت ضمن تسويات سابقة."
									: "VitesLink may suspend or terminate an affiliate account at any time, with or without notice, for violation of these rules or for any other reason at its discretion. Commission earned through fraudulent, abusive, or ineligible activity may be withheld or reversed, including from prior settlements."}
							</Typography>
						</Box>

						{/* Section 9 */}
						<Box className={"section"}>
							<Typography className={"section-title"}>
								{isAr ? "9. تعديلات هذه القواعد" : "9. Changes to These Rules"}
							</Typography>
							<Typography className={"section-body"}>
								{isAr
									? "تحتفظ VitesLink بحق تحديث هذه القواعد أو معدلات العمولة أو جدول التسوية في أي وقت. تسري التعديلات فور نشرها على هذه الصفحة. استمرارك في المشاركة بالبرنامج بعد أي تحديث يُعد موافقة منك على القواعد المعدّلة."
									: "VitesLink reserves the right to update these rules, commission rates, or the settlement schedule at any time. Changes take effect immediately upon posting to this page. Continued participation in the affiliate program after any update constitutes your acceptance of the revised rules."}
							</Typography>
						</Box>

						{/* Section 10 - Contact */}
						<Box className={"section"}>
							<Typography className={"section-title"}>{isAr ? "10. تواصل معنا" : "10. Contact Us"}</Typography>
							<Typography className={"section-body"} mb={1}>
								{isAr
									? "لأي استفسارات حول برنامج التسويق بالعمولة أو كود الخصم الخاص بك أو إحدى التسويات:"
									: "For questions about the affiliate program, your coupon code, or a settlement:"}
							</Typography>
							<Box className={"contact-box"}>
								<Typography fontSize={"14px"} color={"#555"}>
									{isAr ? "البريد الإلكتروني:" : "Email:"} <a href="mailto:info@viteslink.com">info@viteslink.com</a>
								</Typography>
							</Box>
						</Box>
					</Box>
				</Container>
			</AffiliateRulesSection>
		</Box>
	);
};

export default AffiliateRules;
